// The page's side of the PDF worker: reads the files and sends their bytes.
import { wrap, type Remote } from "comlink";
import { kindOf } from "@/lib/files";
import { jpegOrientation } from "@/lib/image/info";
import {
  LINE_HEIGHT,
  type FontFiles,
  type Item,
  type TextItem,
} from "@/lib/pdf/items";
import { fontInfo, type FontId } from "@/lib/pdf/fonts";
import type { ImagesToPdfOptions, PdfImage } from "@/lib/pdf/layout";
import type { PagePlan } from "@/lib/pdf/page-plan";
import { runImageJob } from "@/workers/image-client";
import type { Groups } from "@/lib/pdf/ranges";
import type {
  PageNumberOptions,
  WatermarkOptions,
} from "@/lib/pdf/stamp-options";
import type { PdfWorkerApi } from "@/workers/pdf.worker";

let worker: Remote<PdfWorkerApi> | null = null;

function pdfWorker() {
  worker ??= wrap<PdfWorkerApi>(
    new Worker(new URL("./pdf.worker.ts", import.meta.url), { type: "module" }),
  );
  return worker;
}

const bytesOf = async (file: File) => ({
  name: file.name,
  bytes: new Uint8Array(await file.arrayBuffer()),
});

export async function mergeFiles(files: File[]): Promise<Blob> {
  const out = await pdfWorker().merge(await Promise.all(files.map(bytesOf)));
  return new Blob([out as Uint8Array<ArrayBuffer>], {
    type: "application/pdf",
  });
}

const asPdf = (bytes: Uint8Array) =>
  new Blob([bytes as Uint8Array<ArrayBuffer>], { type: "application/pdf" });

export async function rebuildFile(file: File, pages: PagePlan[]) {
  return asPdf(await pdfWorker().rebuild(await bytesOf(file), pages));
}

export async function splitFile(file: File, groups: Groups) {
  const outs = await pdfWorker().split(await bytesOf(file), groups);
  return outs.map(asPdf);
}

/** pdf-lib embeds only JPG and PNG and ignores EXIF orientation, so upright
 *  JPGs and PNGs go in untouched; everything else is redrawn by the image worker. */
async function toPdfImage(file: File): Promise<PdfImage> {
  const kind = kindOf(file);
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (kind === "png") return { bytes, type: "png" };
  if (kind === "jpg" && jpegOrientation(bytes) === 1) {
    return { bytes, type: "jpg" };
  }
  // WebP, GIF and BMP may have see-through parts: keep them as PNG.
  const type =
    kind === "webp" || kind === "gif" || kind === "bmp" ? "png" : "jpg";
  const out = await runImageJob(file, { format: type, quality: 0.92 });
  return { bytes: new Uint8Array(await out.blob.arrayBuffer()), type };
}

export async function imagesToPdfFile(
  files: File[],
  options: ImagesToPdfOptions,
  onProgress: (done: number) => void,
): Promise<Blob> {
  const images: PdfImage[] = [];
  for (const [i, file] of files.entries()) {
    try {
      images.push(await toPdfImage(file));
    } catch (e) {
      throw new Error(`${file.name}: ${(e as Error).message}`);
    }
    onProgress(i + 1);
  }
  return asPdf(await pdfWorker().fromImages(images, options));
}

/** CSS font-family for a font on the editor screen. */
export const cssFont = (id: FontId) => fontInfo(id).css;

const fileCache = new Map<FontId, Promise<Uint8Array>>();

/** A Google font's file, fetched once from this site (public/fonts/). */
function fontFile(id: FontId): Promise<Uint8Array> {
  let bytes = fileCache.get(id);
  if (!bytes) {
    const url = fontInfo(id).file!;
    bytes = fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.arrayBuffer();
      })
      .then((b) => new Uint8Array(b))
      .catch(() => {
        fileCache.delete(id);
        throw new Error(
          `The font “${fontInfo(id).label}” couldn't be loaded. Check your connection and try again.`,
        );
      });
    fileCache.set(id, bytes);
  }
  return bytes;
}

const screenFonts = new Map<FontId, Promise<void>>();

/** Makes a Google font usable on the editor screen. Built-in fonts resolve at once. */
export function loadScreenFont(id: FontId): Promise<void> {
  const info = fontInfo(id);
  if (!info.file) return Promise.resolve();
  let ready = screenFonts.get(id);
  if (!ready) {
    ready = fontFile(id).then(async (bytes) => {
      const face = new FontFace(
        `PDF ${info.label}`,
        bytes as Uint8Array<ArrayBuffer>,
      );
      await face.load();
      document.fonts.add(face);
    });
    ready.catch(() => screenFonts.delete(id));
    screenFonts.set(id, ready);
  }
  return ready;
}

/** Text the PDF can't hold as real text (e.g. Hindi, or letters the font
 *  lacks) becomes a crisp picture of the text, in the same place and size. */
async function textAsImage(item: TextItem): Promise<Item> {
  const scale = 4; // pixels per point, sharp enough to print
  const lines = item.text.split("\n");
  await loadScreenFont(item.font);
  const font = `${item.size * scale}px ${cssFont(item.font)}`;
  const measure = document.createElement("canvas").getContext("2d")!;
  measure.font = font;
  const width = Math.max(...lines.map((l) => measure.measureText(l).width), 1);
  const lineHeight = item.size * LINE_HEIGHT * scale;
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(width);
  canvas.height = Math.ceil(lineHeight * lines.length);
  const ctx = canvas.getContext("2d")!;
  ctx.font = font;
  ctx.fillStyle = item.color;
  ctx.textBaseline = "top";
  lines.forEach((line, i) => ctx.fillText(line, 0, i * lineHeight));
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Text couldn't be drawn."))),
      "image/png",
    ),
  );
  return {
    id: item.id,
    page: item.page,
    kind: "image",
    format: "png",
    x: item.x,
    y: item.y,
    width: canvas.width / scale,
    height: canvas.height / scale,
    bytes: new Uint8Array(await blob.arrayBuffer()),
  };
}

/** Edit/Sign: draws the added items into the PDF (in the worker). */
export async function annotateFile(file: File, items: Item[]): Promise<Blob> {
  const texts = items.filter(
    (i): i is TextItem => i.kind === "text" && !!i.text.trim(),
  );
  const fonts: FontFiles = {};
  for (const id of new Set(texts.map((t) => t.font))) {
    if (fontInfo(id).file) fonts[id] = await fontFile(id);
  }
  const writable = await pdfWorker().writableAsText(
    texts.map(({ font, text }) => ({ font, text })),
    fonts,
  );
  const asText = new Set(texts.filter((_, i) => writable[i]));
  const ready: Item[] = [];
  for (const item of items) {
    if (item.kind === "text") {
      if (!item.text.trim()) continue;
      ready.push(asText.has(item) ? item : await textAsImage(item));
    } else if (item.kind === "image") {
      const bytes =
        item.bytes ?? new Uint8Array(await item.blob!.arrayBuffer());
      ready.push({ ...item, blob: undefined, bytes });
    } else {
      ready.push(item);
    }
  }
  return asPdf(await pdfWorker().flatten(await bytesOf(file), ready, fonts));
}

/** Watermark (Phase 5, browser): text on every page, in the worker. */
export async function watermarkFile(file: File, options: WatermarkOptions) {
  return asPdf(await pdfWorker().watermark(await bytesOf(file), options));
}

/** Page numbers (Phase 5, browser), in the worker. */
export async function pageNumbersFile(file: File, options: PageNumberOptions) {
  return asPdf(await pdfWorker().pageNumbers(await bytesOf(file), options));
}
